/**
 * Phase 4 — block presentation settings (OD-4: lean `settings` JSONB).
 *
 * Defaults MUST be no-ops so unconfigured tenants (HFF) render identically.
 * Only apply CSS when a setting is explicitly set to a recognized value.
 */

export const OBJECT_FIT_VALUES = ['cover', 'contain', 'fill', 'none', 'scale-down'];

/** Map object-fit → CSS background-size for Hero (bg-image). */
const BG_SIZE_FOR_FIT = {
  cover: 'cover',
  contain: 'contain',
  fill: '100% 100%',
  none: 'auto',
  'scale-down': 'contain',
};

export function normalizeObjectFit(value) {
  if (value == null || value === '') return null;
  const v = String(value).toLowerCase().trim();
  return OBJECT_FIT_VALUES.includes(v) ? v : null;
}

export function normalizeObjectPosition(value) {
  if (value == null || value === '') return null;
  const v = String(value).trim();
  // Allow simple keywords and percentage/length pairs; reject obviously unsafe chars.
  if (!/^[a-zA-Z0-9.%\s_-]+$/.test(v)) return null;
  return v;
}

export function normalizeImageHeight(value) {
  if (value == null || value === '') return null;
  const v = String(value).trim();
  if (v === 'auto') return 'auto';
  // e.g. 400px, 70vh, 50%, 12rem
  if (/^\d+(\.\d+)?(px|vh|vw|%|rem|em)$/.test(v)) return v;
  return null;
}

export function isFullBleed(settings = {}) {
  const v = settings.fullBleed;
  return v === true || v === 'true' || v === 1 || v === '1';
}

/**
 * Resolve presentation for Hero / Image / Gallery.
 * @param {object} settings — block.settings
 * @param {{ defaultFit?: string|null, defaultPosition?: string|null, defaultHeight?: string|null }} defaults
 *   Defaults are the *current hardcoded* values. Pass null when the block
 *   currently leaves the property unset (so we must not emit CSS).
 */
export function resolvePresentation(settings = {}, defaults = {}) {
  const {
    defaultFit = null,
    defaultPosition = null,
    defaultHeight = null,
  } = defaults;

  const fit = normalizeObjectFit(settings.objectFit) ?? defaultFit;
  const position = normalizeObjectPosition(settings.objectPosition) ?? defaultPosition;
  const height = normalizeImageHeight(settings.imageHeight) ?? defaultHeight;
  const fullBleed = isFullBleed(settings);

  return {
    objectFit: fit,
    objectPosition: position,
    imageHeight: height,
    fullBleed,
    /** Explicitly set by tenant (not default) — useful for Style panel / tests */
    hasObjectFit: normalizeObjectFit(settings.objectFit) != null,
    hasObjectPosition: normalizeObjectPosition(settings.objectPosition) != null,
    hasImageHeight: normalizeImageHeight(settings.imageHeight) != null,
  };
}

/** background-size for Hero when an objectFit is active. */
export function backgroundSizeForFit(objectFit) {
  if (!objectFit) return null;
  return BG_SIZE_FOR_FIT[objectFit] || null;
}

/**
 * CSS for escaping a centered max-width wrapper (same pattern as Custom HTML hack).
 * Applied only when fullBleed is true — absent/false → no styles (HFF no-op).
 */
export const FULL_BLEED_CSS = `
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  max-width: 100vw;
  box-sizing: border-box;
`;
